// components/ui/Pagination.jsx
import React from 'react';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, MoreHorizontal } from 'lucide-react';
import { Button }  from './';
import { Select } from './Select';

const Pagination = ({
    currentPage = 1,
    totalPages = 1,
    onPageChange = () => { },
    pageSize = 20,
    onPageSizeChange = () => { },
    totalItems = 0,
    pageSizeOptions = [10, 20, 50, 100],
    showPageSizeSelector = true,
    showPageNumbers = true,
    showFirstLast = true,
    maxVisiblePages = 7,
    className = ''
}) => {
    const startItem = totalItems === 0 ? 0 : (currentPage - 1) * pageSize + 1;
    const endItem = Math.min(currentPage * pageSize, totalItems);

    const handlePageChange = (page) => {
        if (page >= 1 && page <= totalPages && page !== currentPage) {
            onPageChange(page);
        }
    };

    const handlePageSizeChange = (newPageSize) => {
        onPageSizeChange(Number(newPageSize));
        // Reset to page 1 when page size changes
        if (currentPage > Math.ceil(totalItems / newPageSize)) {
            onPageChange(1);
        }
    };

    // Generate page numbers to display
    const generatePageNumbers = () => {
        const pages = [];
        const halfVisible = Math.floor(maxVisiblePages / 2);

        let startPage = Math.max(1, currentPage - halfVisible);
        let endPage = Math.min(totalPages, currentPage + halfVisible);

        // Adjust if we're near the beginning or end
        if (endPage - startPage + 1 < maxVisiblePages) {
            if (startPage === 1) {
                endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
            } else {
                startPage = Math.max(1, endPage - maxVisiblePages + 1);
            }
        }

        // Add first page and ellipsis if needed
        if (startPage > 1) {
            pages.push(1);
            if (startPage > 2) {
                pages.push('ellipsis-start');
            }
        }

        // Add visible page numbers
        for (let i = startPage; i <= endPage; i++) {
            pages.push(i);
        }

        // Add ellipsis and last page if needed
        if (endPage < totalPages) {
            if (endPage < totalPages - 1) {
                pages.push('ellipsis-end');
            }
            pages.push(totalPages);
        }

        return pages;
    };

    const pageNumbers = generatePageNumbers();

    if (totalPages <= 1 && !showPageSizeSelector) {
        return null;
    }

    return (
        <div className={`flex flex-col sm:flex-row items-center justify-between gap-4 p-4 bg-white border-t border-gray-200 ${className}`}>
            {/* Results info and page size selector */}
            <div className="flex flex-col sm:flex-row items-center gap-4">
                <div className="text-sm text-gray-700 order-2 sm:order-1">
                    {totalItems === 0 ? (
                        'No results found'
                    ) : (
                        <>
                            Showing <span className="font-medium">{startItem.toLocaleString()}</span> to{' '}
                            <span className="font-medium">{endItem.toLocaleString()}</span> of{' '}
                            <span className="font-medium">{totalItems.toLocaleString()}</span> results
                        </>
                    )}
                </div>

                {showPageSizeSelector && totalItems > 0 && (
                    <div className="flex items-center gap-2 order-1 sm:order-2">
                        <label htmlFor="page-size" className="text-sm text-gray-600 whitespace-nowrap">
                            Rows per page:
                        </label>
                        <Select
                            id="page-size"
                            value={pageSize}
                            onChange={(e) => handlePageSizeChange(e.target.value)}
                            className="w-20"
                            options={pageSizeOptions.map(size => ({
                                value: size,
                                label: size.toString()
                            }))}
                        />
                    </div>
                )}
            </div>

            {/* Pagination controls */}
            {totalPages > 1 && (
                <div className="flex items-center gap-1">
                    {/* First page button */}
                    {showFirstLast && (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handlePageChange(1)}
                            disabled={currentPage === 1}
                            className="p-2"
                            title="First page"
                        >
                            <ChevronsLeft className="w-4 h-4" />
                        </Button>
                    )}

                    {/* Previous page button */}
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="p-2"
                        title="Previous page"
                    >
                        <ChevronLeft className="w-4 h-4" />
                    </Button>

                    {/* Page numbers */}
                    {showPageNumbers && (
                        <div className="flex items-center gap-1 mx-2">
                            {pageNumbers.map((page, index) => {
                                if (typeof page === 'string' && page.startsWith('ellipsis')) {
                                    return (
                                        <div key={page} className="px-2 py-1">
                                            <MoreHorizontal className="w-4 h-4 text-gray-400" />
                                        </div>
                                    );
                                }

                                return (
                                    <Button
                                        key={page}
                                        variant={page === currentPage ? 'primary' : 'outline'}
                                        size="sm"
                                        onClick={() => handlePageChange(page)}
                                        className={`min-w-[2.5rem] ${page === currentPage
                                                ? 'bg-blue-600 text-white border-blue-600'
                                                : 'hover:bg-gray-50'
                                            }`}
                                    >
                                        {page}
                                    </Button>
                                );
                            })}
                        </div>
                    )}

                    {/* Next page button */}
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className="p-2"
                        title="Next page"
                    >
                        <ChevronRight className="w-4 h-4" />
                    </Button>

                    {/* Last page button */}
                    {showFirstLast && (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handlePageChange(totalPages)}
                            disabled={currentPage === totalPages}
                            className="p-2"
                            title="Last page"
                        >
                            <ChevronsRight className="w-4 h-4" />
                        </Button>
                    )}
                </div>
            )}

            {/* Mobile-only page info */}
            {totalPages > 1 && (
                <div className="sm:hidden text-sm text-gray-600">
                    Page {currentPage} of {totalPages}
                </div>
            )}
        </div>
    );
};

// Simple pagination variant for minimal use cases
export const SimplePagination = ({
    currentPage,
    totalPages,
    onPageChange,
    className = ''
}) => {
    return (
        <div className={`flex items-center justify-center gap-2 p-4 ${className}`}>
            <Button
                variant="outline"
                size="sm"
                onClick={() => onPageChange(currentPage - 1)}
                disabled={currentPage === 1}
            >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Previous
            </Button>

            <span className="px-4 py-2 text-sm text-gray-600">
                Page {currentPage} of {totalPages}
            </span>

            <Button
                variant="outline"
                size="sm"
                onClick={() => onPageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
            >
                Next
                <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
        </div>
    );
};

// Compact pagination for tables with limited space
export const CompactPagination = ({
    currentPage,
    totalPages,
    onPageChange,
    totalItems,
    pageSize,
    className = ''
}) => {
    const startItem = (currentPage - 1) * pageSize + 1;
    const endItem = Math.min(currentPage * pageSize, totalItems);

    return (
        <div className={`flex items-center justify-between p-2 text-sm ${className}`}>
            <div className="text-gray-600">
                {startItem}-{endItem} of {totalItems}
            </div>

            <div className="flex items-center gap-1">
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onPageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="p-1"
                >
                    <ChevronLeft className="w-4 h-4" />
                </Button>

                <div className="px-2 py-1 min-w-[3rem] text-center">
                    {currentPage}/{totalPages}
                </div>

                <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onPageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="p-1"
                >
                    <ChevronRight className="w-4 h-4" />
                </Button>
            </div>
        </div>
    );
};

export default Pagination;